import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { useNavigate, useLocation, ReactLocation, useMatches } from '@tanstack/react-location';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { ReviewsTypes } from '@frontend/api-reviews';
import { ReviewsAggregatorV1 } from '@frontend/api-reviews-aggregator';
import { NoAccessEmptyState } from '@frontend/assets';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { InfinitePaginatedList, PaginatedListRefType, RenderListItemProps } from '@frontend/components';
import { formatDateString } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { MiniChatConstants, MiniChatHooks } from '@frontend/mini-chat';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useActiveContentPanel } from '@frontend/shared';
import { encodeBase64, decodeBase64 } from '@frontend/string';
import { theme } from '@frontend/theme';
import { ContentPanel, Layouts, ListRow, SkeletonLoader } from '@frontend/design-system';
import { CompactReview } from '../components/compact-review';
import { ReviewHeader } from '../components/review-header';
import { ReviewView } from '../components/review-view';
import { reviewServicesList } from '../constants';
import { useReviewsFilterShallowStore, useReviewsFilterStore } from '../hooks';
import { NoAccessReviewsList } from './no-access-reviews-list';

const PANEL_WIDTH = 400;

function getSelectedReviewPath(location: ReactLocation): string {
  const servicesRegexPart = reviewServicesList.join('|');
  const regex = new RegExp(`/reviews/(${servicesRegexPart})/([^?]+)`);
  const match: RegExpMatchArray | null = location.current.pathname.match(regex);

  return match ? match[2] : '';
}

export const ReviewsPage = () => {
  const paginatedListRef = useRef<PaginatedListRefType>(null);
  const navigate = useNavigate();
  const { t } = useTranslation('reviews');
  const isActive = useActiveContentPanel('/reviews/:site/:id');
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const aclEnforcementEnabled = getFeatureFlagValue('nwx:reviews-fe-acl-enforcement', 'ALL');
  const locationIdsWithACL = useMemo(
    () =>
      aclEnforcementEnabled
        ? filterLocationIdsByACLs({
            locationIds: selectedLocationIds,
            acls: Permission.REVIEW_READ,
          })
        : selectedLocationIds,
    [selectedLocationIds, aclEnforcementEnabled, filterLocationIdsByACLs]
  );
  const { sourceFilter, readFilter, repliedFilter, sortByNewest, startDate, endDate } = useReviewsFilterShallowStore(
    'sourceFilter',
    'readFilter',
    'repliedFilter',
    'sortByNewest',
    'startDate',
    'endDate'
  );

  const matches = useMatches();
  const currentSite = matches[0].params.site;

  useLayoutEffect(() => {
    if (paginatedListRef && paginatedListRef.current) {
      paginatedListRef.current.scrollToTop();
    }
  }, [sourceFilter, readFilter, repliedFilter, sortByNewest, endDate, locationIdsWithACL]);

  const reviewsInfiniteQueryProps = ReviewsAggregatorV1.Queries.useGetAggregatedReviewsQuery({
    request: {
      locations: locationIdsWithACL,
      reviewSource:
        sourceFilter === 'all-reviews'
          ? undefined
          : [ReviewsTypes.ReviewSource[sourceFilter.toUpperCase() as keyof typeof ReviewsTypes.ReviewSource]],
      hasRead: readFilter,
      hasReplied: repliedFilter,
      startDate: formatDateString(startDate),
      endDate: formatDateString(endDate),
      sort: sortByNewest ? ReviewsTypes.SortOrder.DESC : ReviewsTypes.SortOrder.ASC,
    },
    options: {
      select: (data) => ({
        ...data,
        pages: data.pages.map((page) => ({ rows: page.reviews })),
      }),
    },
  });

  useEffect(() => {
    return () => useReviewsFilterStore.reset();
  }, []);

  const reviewsCount = reviewsInfiniteQueryProps.data?.pages.reduce<number>((acc, page) => acc + page.rows.length, 0);

  const { chats, rolledUp } = MiniChatHooks.useMiniChatShallowStore('chats', 'rolledUp');
  const isMobileMiniChats = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const showBottomPadding = !!chats.length && !rolledUp && !isMobileMiniChats;

  return (
    <Layouts.MultiPanel css={{ display: 'flex' }}>
      <ContentPanel
        css={css`
          min-width: ${PANEL_WIDTH}px;
        `}
      >
        <ContentPanel.Header>
          <ReviewHeader />
        </ContentPanel.Header>
        <ContentPanel.Body>
          {!!locationIdsWithACL.length ? (
            <InfinitePaginatedList
              skeletonLoader={<ReviewItemSkeletonLoader />}
              infiniteQueryProps={reviewsInfiniteQueryProps}
              renderListItem={ReviewListItem}
              emptyStateConfig={{
                type: 'reviews',
                header: t('No reviews to display'),
              }}
              paginatedListRef={paginatedListRef}
            />
          ) : (
            <NoAccessReviewsList />
          )}
        </ContentPanel.Body>
      </ContentPanel>
      <ContentPanel
        isActive={isActive}
        onBackClick={() => {
          navigate({ to: `/reviews/${currentSite}` });
        }}
        hasBack={isActive}
        css={css`
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          height: 100%;
          padding-bottom: ${showBottomPadding ? MiniChatConstants.HEADER_HEIGHT : 0}px;
          transition: padding-bottom 300ms ease-in-out;
        `}
      >
        {!locationIdsWithACL.length && selectedLocationIds.length ? (
          <NoAccessEmptyState
            message={t('You do not have permission to view reviews for any of the selected locations')}
          />
        ) : (
          <ReviewView noReviews={!reviewsInfiniteQueryProps.isLoading && reviewsCount === 0} />
        )}
      </ContentPanel>
    </Layouts.MultiPanel>
  );
};

export const ReviewItemSkeletonLoader = () => {
  return (
    <ListRow as='article' css={{ gridTemplateColumns: 'auto 1fr', padding: theme.spacing(3) }}>
      <ListRow.Lead>
        <SkeletonLoader shape='circle' width={40} height={40} />
      </ListRow.Lead>
      <ListRow.Content>
        <section
          css={css`
            display: flex;
            justify-content: space-between;
            margin-bottom: ${theme.spacing(0.5)};
          `}
        >
          <SkeletonLoader height={16} width={100} />
          <SkeletonLoader height={14} width={80} />
        </section>
        <div
          css={css`
            margin-top: ${theme.spacing(0.5)};
          `}
        >
          <SkeletonLoader height={18} width={120} />
        </div>
        <SkeletonLoader
          css={css`
            margin-top: ${theme.spacing(1)};
            margin-bottom: ${theme.spacing(1)};
          `}
          height={14}
          width={200}
        />
        <ListRow.Content.TertiaryTitle
          css={css`
            display: flex;
            align-items: center;
            gap: ${theme.spacing(1)};
          `}
          as='span'
        >
          <SkeletonLoader height={18} width={90} />
          <SkeletonLoader height={18} width={70} />
        </ListRow.Content.TertiaryTitle>
      </ListRow.Content>
    </ListRow>
  );
};

export const ReviewListItem = ({ listItem, index }: RenderListItemProps<ReviewsTypes.ReviewAggregator>): ReactNode => {
  const { getScopeName } = useAppScopeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const matches = useMatches();
  const currentSite = matches[0].params.site;
  const {
    id,
    hasRead,
    profileImageUrl,
    canReply,
    recommendationType,
    dateModified,
    locationId,
    site,
    datePublished,
    comments,
    ...review
  } = listItem;
  const locationName = getScopeName(locationId);
  const onClick = () => {
    navigate({ to: `/reviews/${currentSite}/${encodeBase64(id)}?type=${site}` });
  };
  const selectedReviewPath = getSelectedReviewPath(location);
  const selectedReviewId = decodeBase64(selectedReviewPath);

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <CompactReview
        isSelected={selectedReviewId === id}
        onClick={onClick}
        profileImage={profileImageUrl}
        recommendation={recommendationType || ''}
        size='medium'
        showLogo
        datePublished={datePublished}
        locationName={locationName}
        showNotificationBubble={!hasRead}
        key={`pl-item${index}`}
        siteName={site}
        {...review}
      />
    </div>
  );
};
